/**
 * EditPaymentModal - Friendly modal explaining Shopify payment update process
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Alert,
  keyframes,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QuizButton } from "./shared/QuizButton";
import { ACCOUNT_COLORS } from "../theme/colors";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(5deg); }
`;

type EditPaymentModalProps = {
  open: boolean;
  onClose: () => void;
  onSendEmail: (paymentMethodId?: string) => Promise<{ ok: boolean; error?: string }>;
  paymentInfo: {
    id?: string | number;
    brand?: string;
    last4?: string;
  } | null;
};

export function EditPaymentModal({
  open,
  onClose,
  onSendEmail,
  paymentInfo,
}: EditPaymentModalProps) {
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const brand = paymentInfo?.brand
    ? paymentInfo.brand.charAt(0).toUpperCase() + paymentInfo.brand.slice(1).toLowerCase()
    : "Card";
  const last4 = paymentInfo?.last4 || "****";

  const handleSendEmail = async () => {
    setSending(true);
    setError(null);
    try {
      const paymentMethodId = paymentInfo?.id ? String(paymentInfo.id) : undefined;
      const result = await onSendEmail(paymentMethodId);
      if (result.ok) {
        setEmailSent(true);
      } else {
        setError(result.error || "Failed to send email. Please try again.");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setEmailSent(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          background: "linear-gradient(135deg, #FFFCF5 0%, #FAF5EB 50%, #F3EBDD 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative" }}>
        {/* Decorative Paw Prints */}
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: 60,
            fontSize: 28,
            opacity: 0.3,
            animation: `${float} 3s ease-in-out infinite`,
          }}
        >
          🐾
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 20,
            fontSize: 24,
            opacity: 0.25,
            animation: `${float} 4s ease-in-out infinite 0.5s`,
          }}
        >
          🐾
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: ACCOUNT_COLORS.textPrimary,
            backgroundColor: "rgba(255,255,255,0.7)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.9)",
            },
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #FFFFFF 0%, #F0F7FF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              }}
            >
              <CreditCardIcon
                sx={{ fontSize: 40, color: ACCOUNT_COLORS.primary }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 22, md: 26 },
                fontWeight: 900,
                color: ACCOUNT_COLORS.textPrimary,
                mb: 0.5,
              }}
            >
              Update Payment Method
            </Typography>
          </Box>

          {/* Card Info Box */}
          <Box
            sx={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: "16px",
              p: 2.5,
              mb: 3,
              border: `2px solid ${ACCOUNT_COLORS.primary}`,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: ACCOUNT_COLORS.textTertiary,
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 0.5,
              }}
            >
              Current Payment Method
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                color: ACCOUNT_COLORS.textPrimary,
              }}
            >
              {brand} •••• {last4}
            </Typography>
          </Box>

          {/* Explanation */}
          <Box
            sx={{
              background: "rgba(255,255,255,0.7)",
              borderRadius: "16px",
              p: 2.5,
              mb: 3,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LockIcon sx={{ fontSize: 22, color: "#2E7D32" }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: ACCOUNT_COLORS.textPrimary,
                    mb: 0.5,
                  }}
                >
                  Secure Updates via Shopify
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: ACCOUNT_COLORS.textPrimary,
                    opacity: 0.8,
                    lineHeight: 1.6,
                  }}
                >
                  For your security, payment updates are handled directly by Shopify.
                  We'll send you a secure email with a link to update your card details safely.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Success Message */}
          {emailSent && (
            <Alert
              icon={<CheckCircleIcon sx={{ fontSize: 24 }} />}
              severity="success"
              sx={{
                mb: 3,
                borderRadius: "12px",
                background: "#E8F5E9",
                border: "1px solid #A5D6A7",
                "& .MuiAlert-icon": {
                  alignItems: "center",
                  color: "#2E7D32",
                },
                "& .MuiAlert-message": {
                  fontSize: 14,
                  fontWeight: 600,
                },
              }}
            >
              Email sent! Check your inbox for the secure update link from Shopify.
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: "12px",
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Action Button */}
          <QuizButton
            variant="primary"
            fullWidth
            startIcon={emailSent ? <CheckCircleIcon /> : <EmailIcon />}
            onClick={handleSendEmail}
            disabled={sending || emailSent}
            sx={{
              py: 1.5,
              fontSize: 16,
              borderRadius: "50px",
              background: emailSent
                ? "#4CAF50"
                : `linear-gradient(135deg, ${ACCOUNT_COLORS.primary} 0%, #A64A08 100%)`,
            }}
          >
            {sending ? "Sending..." : emailSent ? "Email Sent!" : "Send Update Email"}
          </QuizButton>

          {/* Footer Note */}
          <Typography
            sx={{
              fontSize: 12,
              color: ACCOUNT_COLORS.textTertiary,
              textAlign: "center",
              mt: 2,
            }}
          >
            The email will arrive from Shopify within a few minutes
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
